import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, CheckCircle, XCircle, Thermometer, Zap, Sun,
  AlertTriangle, FileText, ChevronRight,
} from 'lucide-react';
import { api } from '@/utils/api';

interface DiagnosisCheck {
  issue: string;
  found: boolean;
  action: 'repair' | 'replace' | 'adjust' | 'clean' | '';
  notes: string;
}

const DIAGNOSIS_OPTIONS_SSWATER = [
  { issue: 'Leakage from tank', actions: ['repair', 'replace'] },
  { issue: 'Temperature not rising', actions: ['repair', 'clean'] },
  { issue: 'Dents on outer shell', actions: ['repair', 'replace'] },
  { issue: 'Rust/corrosion', actions: ['repair', 'replace'] },
  { issue: 'Water quality issues', actions: ['clean', 'adjust'] },
  { issue: 'Noise/vibration', actions: ['repair', 'adjust'] },
];

const DIAGNOSIS_OPTIONS_HEATPUMP = [
  { issue: 'No hot water', actions: ['repair', 'replace'] },
  { issue: 'Low water temperature', actions: ['repair', 'adjust'] },
  { issue: 'Reverse line (cold water hot)', actions: ['repair', 'replace'] },
  { issue: 'High power consumption', actions: ['repair', 'adjust'] },
  { issue: 'Capacity problems', actions: ['repair', 'replace'] },
  { issue: 'Compressor not working', actions: ['repair', 'replace'] },
  { issue: 'Noise/vibration', actions: ['repair', 'adjust'] },
  { issue: 'Error codes displayed', actions: ['repair'] },
];

const DIAGNOSIS_OPTIONS_SWH = [
  { issue: 'Leakage from tank', actions: ['repair', 'replace'] },
  { issue: 'Low heating efficiency', actions: ['repair', 'clean'] },
  { issue: 'Vacuum tube breakage', actions: ['replace'] },
  { issue: 'Collector issues', actions: ['repair', 'replace'] },
  { issue: 'Controller malfunction', actions: ['repair', 'replace'] },
  { issue: 'Temperature sensor failure', actions: ['repair', 'replace'] },
  { issue: 'Water leakage at joints', actions: ['repair'] },
];

type ProductType = 'ss_water_tank' | 'heat_pump' | 'swh';

export function ProductDiagnosisPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ProductType>('ss_water_tank');
  const [diagnosis, setDiagnosis] = useState<DiagnosisCheck[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [generalNotes, setGeneralNotes] = useState('');

  const getOptions = (type: ProductType) => {
    switch (type) {
      case 'heat_pump': return DIAGNOSIS_OPTIONS_HEATPUMP;
      case 'swh': return DIAGNOSIS_OPTIONS_SWH;
      default: return DIAGNOSIS_OPTIONS_SSWATER;
    }
  };

  const options = getOptions(activeTab);

  const updateDiagnosis = (index: number, field: keyof DiagnosisCheck, value: any) => {
    setDiagnosis((prev) => {
      const updated = [...prev];
      if (!updated[index]) {
        updated[index] = { issue: options[index].issue, found: false, action: '', notes: '' };
      }
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await api.patch(`/service-sop/complaints/${id}/diagnosis`, {
        productType: activeTab,
        diagnosis: diagnosis.filter(d => d !== undefined),
        notes: generalNotes,
      });
      navigate(`/service-tickets/${id}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save diagnosis');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: 'ss_water_tank', label: 'SS Water Tank', icon: Thermometer },
    { key: 'heat_pump', label: 'Heat Pump', icon: Zap },
    { key: 'swh', label: 'Solar Water Heater', icon: Sun },
  ] as const;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Product Diagnosis</h1>
        <p className="text-sm text-[var(--text-muted)]">Diagnose issues based on product type</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setDiagnosis([]); }}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-[var(--ink-0)] text-white'
                : 'border border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {options.map((opt, idx) => (
          <div
            key={opt.issue}
            className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-[var(--text-primary)]">{opt.issue}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => updateDiagnosis(idx, 'found', true)}
                  className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    diagnosis[idx]?.found === true
                      ? 'bg-green-600 text-white'
                      : 'border border-green-300 text-green-700 hover:bg-green-50'
                  }`}
                >
                  <XCircle size={12} /> Found
                </button>
                <button
                  onClick={() => updateDiagnosis(idx, 'found', false)}
                  className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    diagnosis[idx]?.found === false
                      ? 'bg-gray-600 text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <CheckCircle size={12} /> Not Found
                </button>
              </div>
            </div>

            {diagnosis[idx]?.found && (
              <div className="ml-4 space-y-3 border-l-2 border-[var(--ink-3)] pl-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Action Taken</label>
                  <div className="flex gap-2">
                    {opt.actions.map((action) => (
                      <button
                        key={action}
                        onClick={() => updateDiagnosis(idx, 'action', action)}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                          diagnosis[idx]?.action === action
                            ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]'
                            : 'border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]'
                        }`}
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Notes</label>
                  <input
                    type="text"
                    value={diagnosis[idx]?.notes || ''}
                    onChange={(e) => updateDiagnosis(idx, 'notes', e.target.value)}
                    placeholder="Specific observations..."
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none"
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        {/* General Notes */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-2 flex items-center gap-2">
            <FileText size={16} />
            General Observations
          </h3>
          <textarea
            value={generalNotes}
            onChange={(e) => setGeneralNotes(e.target.value)}
            rows={4}
            placeholder="Overall condition, additional findings, customer feedback..."
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none"
          />
        </div>

        {/* Summary */}
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <AlertTriangle size={16} />
            <span>Issues Found: {diagnosis.filter(d => d?.found).length}</span>
            <span className="mx-2">|</span>
            <span>Actions Required: {diagnosis.filter(d => d?.action).length}</span>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(`/service-tickets/${id}`)}
            className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <ChevronRight size={14} />}
            Save Diagnosis & Proceed
          </button>
        </div>
      </div>
    </div>
  );
}